import { useState, useEffect, RefObject } from 'react'

/**
 * IntersectionObserver helper. Ref targets often mount after async data
 * (e.g. InvestorMatchCard spinner → card). Keep retrying until the node exists;
 * a fixed attempt budget left observers disconnected and gated UI stuck (match
 * scores hard-coded to "0" until animateIn, which never fired).
 */
const useOnScreen = (ref: RefObject<Element | null>, rootMargin = '0px') => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    let observer: IntersectionObserver | null = null
    let retryId: ReturnType<typeof setTimeout> | undefined
    let cancelled = false

    const connect = () => {
      if (cancelled) {
        return
      }

      const currentElement = ref?.current
      if (!currentElement) {
        retryId = setTimeout(connect, 50)
        return
      }

      observer = new IntersectionObserver(
        ([entry]) => {
          setIsVisible(entry.isIntersecting)
        },
        {
          rootMargin,
        }
      )
      observer.observe(currentElement)

      // Immediate check — IO may not fire a callback until a scroll/layout pass.
      const rect = currentElement.getBoundingClientRect()
      const vh = window.innerHeight || document.documentElement.clientHeight
      const vw = window.innerWidth || document.documentElement.clientWidth
      const intersects =
        rect.bottom > 0 && rect.right > 0 && rect.top < vh && rect.left < vw
      if (intersects) {
        setIsVisible(true)
      }
    }

    connect()

    return () => {
      cancelled = true
      if (retryId) {
        clearTimeout(retryId)
      }
      observer?.disconnect()
    }
  }, [ref, rootMargin])

  return isVisible
}

export default useOnScreen
